(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.DDCGiftFinderEngine = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const RECIPIENT_TAGS = {
    ella: ['Para Ella'],
    el: ['Para Él'],
    ninos: ['Niños'],
    pareja: ['Para Ella', 'Para Él'],
  };

  const OCCASION_TAGS = {
    cumpleanos: ['Cumpleaños'],
    amor: ['Amor y Aniversario'],
    saludable: ['Saludables'],
    sorprender: [],
  };

  const LABELS = {
    recipient: { ella: 'para ella', el: 'para él', ninos: 'para un niño', pareja: 'para mi pareja' },
    occasion: { cumpleanos: 'cumpleaños', amor: 'amor o aniversario', saludable: 'bienestar o cuidado', sorprender: 'sorprender sin una fecha especial' },
    budget: { economico: 'hasta $80.000', medio: '$80.000 a $130.000', premium: 'más de $130.000' },
  };

  function getAvailableVariant(product) {
    const variants = product && Array.isArray(product.variants) ? product.variants : [];
    return variants.find(variant => variant.available) || null;
  }

  function numberPrice(product) {
    const variant = getAvailableVariant(product);
    return Number(variant && variant.price) || 0;
  }

  function isAvailable(product) {
    return Boolean(getAvailableVariant(product));
  }

  function inBudget(price, budget) {
    if (budget === 'economico') return price <= 80000;
    if (budget === 'medio') return price > 80000 && price <= 130000;
    if (budget === 'premium') return price > 130000;
    return true;
  }

  function hasAnyTag(productTags, expectedTags) {
    if (!expectedTags.length) return false;
    return expectedTags.some(tag => productTags.includes(tag));
  }

  function evaluate(product, answers) {
    const tags = Array.isArray(product.tags) ? product.tags : [];
    const recipientTags = RECIPIENT_TAGS[answers.recipient] || [];
    const occasionTags = OCCASION_TAGS[answers.occasion] || [];
    const matches = {
      recipient: hasAnyTag(tags, recipientTags),
      occasion: answers.occasion === 'sorprender' ? true : hasAnyTag(tags, occasionTags),
      budget: inBudget(numberPrice(product), answers.budget),
    };
    let score = 0;
    if (matches.recipient) score += 60;
    if (matches.occasion) score += answers.occasion === 'sorprender' ? 12 : 40;
    if (matches.budget) score += 25;
    if (answers.recipient === 'pareja' && tags.includes('Amor y Aniversario')) score += 18;
    if (tags.includes('Más Vendidos')) score += 7;
    if (tags.includes('configurador-desayuno')) score += 3;
    return { product, variant: getAvailableVariant(product), score, matches };
  }

  function recommendProducts(products, answers, limit) {
    const max = Number(limit) || 3;
    const ranked = products
      .filter(product => isAvailable(product))
      .filter(product => !(product.tags || []).includes('Adicionales'))
      .map(product => {
        const item = evaluate(product, answers);
        return Object.assign(item, { price: numberPrice(product) });
      })
      .sort((a, b) => b.score - a.score || a.price - b.price || String(a.product.id).localeCompare(String(b.product.id)));

    if (!ranked.length) return [];

    const chosen = [];
    const take = (item, slot) => {
      if (!item || chosen.some(entry => entry.product.id === item.product.id)) return;
      chosen.push(Object.assign({}, item, { slot }));
    };

    take(ranked[0], 'best');

    const remaining = () => ranked.filter(item => !chosen.some(entry => entry.product.id === item.product.id));
    const economic = remaining()
      .filter(item => item.matches.budget)
      .sort((a, b) => {
        const aSemantic = (a.matches.recipient ? 60 : 0) + (a.matches.occasion ? 40 : 0);
        const bSemantic = (b.matches.recipient ? 60 : 0) + (b.matches.occasion ? 40 : 0);
        return bSemantic - aSemantic || a.price - b.price || b.score - a.score;
      })[0]
      || remaining().sort((a, b) => a.price - b.price || b.score - a.score)[0];
    take(economic, 'economic');

    const special = remaining().sort((a, b) => {
      const aSemantic = (a.matches.recipient ? 60 : 0) + (a.matches.occasion ? 40 : 0);
      const bSemantic = (b.matches.recipient ? 60 : 0) + (b.matches.occasion ? 40 : 0);
      return bSemantic - aSemantic || b.price - a.price || b.score - a.score;
    })[0];
    take(special, 'special');

    for (const item of ranked) {
      if (chosen.length >= max) break;
      take(item, 'alternative');
    }
    return chosen.slice(0, max);
  }

  function buildReason(matches) {
    if (matches.recipient && matches.occasion && matches.budget) return 'Coincide con la persona, la ocasión y tu presupuesto.';
    if (matches.recipient && matches.budget) return 'Buena opción para esa persona dentro de tu presupuesto.';
    if (matches.recipient && matches.occasion) return 'Encaja con la persona y el momento que quieres celebrar.';
    if (matches.occasion && matches.budget) return 'Encaja con la ocasión y se mantiene dentro de tu presupuesto.';
    if (matches.recipient) return 'Una opción pensada para esa persona.';
    if (matches.budget) return 'Una alternativa dentro de tu presupuesto.';
    return 'Una alternativa popular que puede funcionar para tu regalo.';
  }

  function buildWhatsAppUrl(phone, answers) {
    const recipient = LABELS.recipient[answers.recipient] || 'una persona especial';
    const occasion = LABELS.occasion[answers.occasion] || 'una ocasión especial';
    const budget = LABELS.budget[answers.budget] || 'un presupuesto por definir';
    const message = `Hola, estoy en su página web buscando un regalo ${recipient}, para ${occasion}, con un presupuesto de ${budget}. ¿Me ayudan a elegir?`;
    return `https://wa.me/${String(phone).replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
  }

  return {
    RECIPIENT_TAGS,
    OCCASION_TAGS,
    LABELS,
    getAvailableVariant,
    recommendProducts,
    buildReason,
    buildWhatsAppUrl,
  };
});
